/**
 * TeamSystem Impact - Infrastructure Configuration Store
 * Zustand store per parametri infrastrutturali configurabili
 * che influenzano i calcoli della Piattaforma Sostenibile.
 */

import { create } from 'zustand';

export interface InfrastructureConfig {
  provider: string;
  region: string;
  instanceType: string;
  database: string;
  pue: number;
  carbonIntensity: number; // gCO2eq/kWh
  wue: number;             // L/kWh
  serverLifetimeYears: number;
  totalHostVCPUs: number;
  serverEmbodiedKgCO2: number;
}

interface InfrastructureState {
  config: InfrastructureConfig;
  setConfig: (config: Partial<InfrastructureConfig>) => void;
  resetToDefaults: () => void;
}

const STORAGE_KEY = 'teamsystem-impact-infrastructure';

export const PROVIDER_PRESETS: Record<string, { regions: { name: string; carbonIntensity: number; pue: number; wue: number }[] }> = {
  Azure: {
    regions: [
      { name: 'Italy North (Milan)', carbonIntensity: 310, pue: 1.12, wue: 1.0 },
      { name: 'West Europe (Netherlands)', carbonIntensity: 386, pue: 1.12, wue: 0.55 },
      { name: 'North Europe (Ireland)', carbonIntensity: 296, pue: 1.12, wue: 0.50 },
      { name: 'France Central (Paris)', carbonIntensity: 56, pue: 1.10, wue: 0.45 },
      { name: 'Sweden Central (Gavle)', carbonIntensity: 15, pue: 1.08, wue: 0.35 },
      { name: 'Germany West Central (Frankfurt)', carbonIntensity: 338, pue: 1.12, wue: 0.55 },
      { name: 'UK South (London)', carbonIntensity: 228, pue: 1.10, wue: 0.50 },
      { name: 'East US (Virginia)', carbonIntensity: 383, pue: 1.12, wue: 0.55 },
      { name: 'West US 2 (Washington)', carbonIntensity: 80, pue: 1.10, wue: 0.40 },
    ],
  },
  AWS: {
    regions: [
      { name: 'eu-south-1 (Milan)', carbonIntensity: 310, pue: 1.15, wue: 1.0 },
      { name: 'eu-west-1 (Ireland)', carbonIntensity: 296, pue: 1.15, wue: 0.50 },
      { name: 'eu-central-1 (Frankfurt)', carbonIntensity: 338, pue: 1.15, wue: 0.55 },
      { name: 'eu-north-1 (Stockholm)', carbonIntensity: 15, pue: 1.10, wue: 0.35 },
      { name: 'eu-west-3 (Paris)', carbonIntensity: 56, pue: 1.12, wue: 0.45 },
      { name: 'us-east-1 (Virginia)', carbonIntensity: 383, pue: 1.15, wue: 0.55 },
      { name: 'us-west-2 (Oregon)', carbonIntensity: 80, pue: 1.10, wue: 0.40 },
    ],
  },
  'Google Cloud': {
    regions: [
      { name: 'europe-west8 (Milan)', carbonIntensity: 310, pue: 1.10, wue: 0.90 },
      { name: 'europe-west1 (Belgium)', carbonIntensity: 155, pue: 1.10, wue: 0.50 },
      { name: 'europe-west4 (Netherlands)', carbonIntensity: 386, pue: 1.10, wue: 0.50 },
      { name: 'europe-north1 (Finland)', carbonIntensity: 71, pue: 1.08, wue: 0.30 },
      { name: 'europe-west9 (Paris)', carbonIntensity: 56, pue: 1.08, wue: 0.45 },
      { name: 'us-east1 (South Carolina)', carbonIntensity: 383, pue: 1.10, wue: 0.55 },
      { name: 'us-west1 (Oregon)', carbonIntensity: 80, pue: 1.08, wue: 0.40 },
    ],
  },
};

export const DEFAULT_CONFIG: InfrastructureConfig = {
  provider: 'Azure',
  region: 'Italy North (Milan)',
  instanceType: 'App Service B1 (1 vCPU, 1.75 GiB RAM)',
  database: 'MongoDB Atlas M10',
  pue: 1.12,
  carbonIntensity: 310,
  wue: 1.0,
  serverLifetimeYears: 6,
  totalHostVCPUs: 64,
  serverEmbodiedKgCO2: 1200,
};

const loadConfig = (): InfrastructureConfig => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_CONFIG, ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_CONFIG };
};

export const useInfrastructureStore = create<InfrastructureState>()((set) => ({
  config: loadConfig(),

  setConfig: (partial) => {
    set((state) => {
      const updated = { ...state.config, ...partial };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return { config: updated };
    });
  },

  resetToDefaults: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({ config: { ...DEFAULT_CONFIG } });
  },
}));
